// Connections (docs/cockpit/spec.md, Setup): the External Assistants, such as Claude or ChatGPT, connected to LAMP,
// per person, with where each signs in from, what it may do and when it was last used. An Owner sees and revokes
// everyone's; anyone else their own. The Application API decides both again, and the assistant's next call after a
// revocation is refused.
import { useCallback, useEffect, useState } from "react";
import { ApiError, type Api, type ConnectionRow } from "../api.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";
import { lastActive } from "../users/Users.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

/** What the assistant is called: the name it gave itself, else where it signs in from. */
const assistantOf = (c: ConnectionRow) => c.name ?? c.host;

export function Connections({ api, personId, now }: { api: Api; personId: string; now?: Date }) {
  const w = useWords();
  const { language } = useLanguage();
  const [connections, setConnections] = useState<readonly ConnectionRow[] | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.connections().then(setConnections, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api]);
  useEffect(load, [load]);

  const revoke = async (connection: ConnectionRow) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      await api.revokeConnection(connection.id);
      setNotice(w.connectionsRevoked(assistantOf(connection)));
      load();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (!connections) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.connectionsLoading}</p>;
  const who = (c: ConnectionRow) => (c.person.id === personId ? w.connectionsYou : (c.person.name ?? c.person.id));
  // Per person, the person's own first, then each connection as it was made.
  const shown = [...connections].sort(
    (a, b) =>
      Number(b.person.id === personId) - Number(a.person.id === personId) ||
      who(a).localeCompare(who(b)) ||
      a.connectedAt.localeCompare(b.connectedAt),
  );
  return (
    <>
      <p className="muted">{w.connectionsIntro}</p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {shown.length === 0 ? (
        <p>{w.connectionsNone}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{w.connectionsPerson}</th>
                <th scope="col">{w.connectionsAssistant}</th>
                <th scope="col">{w.connectionsHost}</th>
                <th scope="col">{w.connectionsMay}</th>
                <th scope="col">{w.connectionsConnected}</th>
                <th scope="col">{w.connectionsLastUsed}</th>
                <th scope="col">
                  <span className="sr-only">{w.connectionsRevoke}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td>{who(c)}</td>
                  <td>
                    <strong>{assistantOf(c)}</strong>
                    <br />
                    <span className="small muted">{c.client}</span>
                  </td>
                  <td>{c.host}</td>
                  <td>{c.scope.split(" ").includes("lamp.act") ? w.connectionsReadAndAsk : w.connectionsRead}</td>
                  <td>{lastActive(c.connectedAt, now, language)}</td>
                  <td>{lastActive(c.lastUsed, now, language)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-small"
                      disabled={busy}
                      aria-label={w.connectionsRevokeOf(assistantOf(c), who(c))}
                      onClick={() => void revoke(c)}
                    >
                      {w.connectionsRevoke}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
